/**
 * Custom error types for EmbarkAI operations
 */

/**
 * Base error class for all EmbarkAI errors
 * Local copy to avoid @embarkai/core dependency in iframe bundle
 */
export class AccountAbstractionError extends Error {
  /** Machine-readable error code for programmatic handling */
  readonly code: string
  readonly details: Record<string, unknown>

  constructor(message: string, code: string, details: Record<string, unknown> = {}) {
    super(message)
    this.code = code
    this.details = details
    this.name = 'LumiaPassportError'

    // Maintain proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AccountAbstractionError)
    }
  }
}

/**
 * Error thrown when user rejects a transaction or signature request
 */
export class UserRejectedError extends AccountAbstractionError {
  static readonly CODE = 'USER_REJECTED' as const

  constructor(message: string = 'User rejected transaction') {
    super(message, UserRejectedError.CODE)
    this.name = 'UserRejectedError'
  }
}

/**
 * Error thrown when the auth session is dead (refresh impossible)
 * and the user must log in again
 */
export class SessionExpiredError extends AccountAbstractionError {
  static readonly CODE = 'SESSION_EXPIRED' as const

  constructor(message: string = 'Session expired, please log in again') {
    super(message, SessionExpiredError.CODE)
    this.name = 'SessionExpiredError'
  }
}

/**
 * Error codes for EmbarkAI operations
 */
export class PolicyViolationError extends AccountAbstractionError {
  static readonly CODE = 'POLICY_VIOLATION' as const

  readonly policyId: string
  readonly rule: string
  readonly reason: string

  constructor(message: string, details: Record<string, unknown> = {}) {
    super(message, PolicyViolationError.CODE, details)
    this.name = 'PolicyViolationError'
    this.policyId = typeof details.policyId === 'string' ? details.policyId : ''
    this.rule = typeof details.rule === 'string' ? details.rule : ''
    this.reason = typeof details.reason === 'string' ? details.reason : ''
  }
}

export const ErrorCodes = {
  MPC_SIGNING_ERROR: 'MPC_SIGNING_ERROR',
  USER_REJECTED: UserRejectedError.CODE,
  SESSION_EXPIRED: SessionExpiredError.CODE,
  POLICY_VIOLATION: PolicyViolationError.CODE,
  POLICY_UNAVAILABLE: 'POLICY_UNAVAILABLE',
  WALLET_REVOKED: 'WALLET_REVOKED',
  DIGEST_MISMATCH: 'DIGEST_MISMATCH',
  MISSING_OPERATION: 'MISSING_OPERATION',
  UNSUPPORTED_CALLDATA: 'UNSUPPORTED_CALLDATA',
  TSS_REQUEST_FAILED: 'TSS_REQUEST_FAILED'
} as const

export interface TssErrorBody {
  error?: string
  code?: string
  message?: string
  policyId?: string
  rule?: string
  reason?: string
  existingOwnerAddress?: string | null
}

export function tssErrorFromBody(status: number, body: TssErrorBody): AccountAbstractionError {
  const tssCode = body.error ?? body.code
  const code = tssCode ? tssCode.toUpperCase() : ErrorCodes.TSS_REQUEST_FAILED
  const message = body.message ?? body.error ?? `API call failed (${status})`
  const details: Record<string, unknown> = { status }
  if (body.policyId !== undefined) details.policyId = body.policyId
  if (body.rule !== undefined) details.rule = body.rule
  if (body.reason !== undefined) details.reason = body.reason
  if (body.existingOwnerAddress) details.existingOwnerAddress = body.existingOwnerAddress
  return createAccountAbstractionError({ message, code, details }) as AccountAbstractionError
}

/**
 * Duck-types by `code` (not `instanceof`) so the check survives bundle boundaries
 */
export function isSessionExpiredError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === ErrorCodes.SESSION_EXPIRED
  )
}

/**
 * Create appropriate error from error code
 * Used to reconstruct errors from iframe postMessage
 */
export function createAccountAbstractionError(params: {
  message: string
  code?: string
  details?: Record<string, unknown>
}): Error {
  const { message, code, details = {} } = params

  if (!code) {
    return new Error(message)
  }

  switch (code) {
    case UserRejectedError.CODE:
      return new UserRejectedError(message)
    case SessionExpiredError.CODE:
      return new SessionExpiredError(message)
    case PolicyViolationError.CODE:
      return new PolicyViolationError(message, details)
    default:
      return new AccountAbstractionError(message, code, details)
  }
}
